import React from 'react';
import { X, Loader2 } from 'lucide-react';
import { Drawer, DrawerContent } from '../UI/drawer';
import { Button } from '../UI/button';
import { cn } from '../../Lib/Cn';

/**
 * Highly modular Alert Drawer component for Mobile views.
 *
 * @param {Object} props
 * @param {boolean} props.open - Controlled open state.
 * @param {Function} props.onOpenChange - Callback when open state changes.
 * @param {Function} [props.onClose] - Optional close callback.
 * @param {React.ReactNode|string} [props.title] - Drawer header title.
 * @param {React.ComponentType|React.ReactNode} [props.icon] - Icon component or node for header.
 * @param {string} [props.iconClassName] - Custom classes for icon.
 * @param {string} [props.iconContainerClassName] - Container classes for icon.
 * @param {string} [props.titleClassName] - Custom classes for title.
 * @param {boolean} [props.showCloseButton=true] - Whether to show the top-right X close button.
 * @param {string|React.ReactNode} [props.image] - Image URL or React element.
 * @param {string} [props.imageAlt=""] - Image alt text.
 * @param {'above-desc'|'below-desc'|'above-header'} [props.imagePosition='above-desc'] - Image position relative to description.
 * @param {string} [props.imageClassName] - Custom classes for image.
 * @param {React.ReactNode|string} [props.description] - Major / primary description.
 * @param {string} [props.descriptionClassName] - Custom classes for primary description.
 * @param {React.ReactNode|string} [props.description2] - Optional secondary description.
 * @param {string} [props.description2ClassName] - Custom classes for secondary description.
 * @param {'row'|'col'|'column'} [props.buttonLayout='row'] - Button layout orientation.
 * @param {string} [props.buttonGap='gap-3'] - Gap between buttons.
 * @param {Object} [props.primaryButton] - Primary button configuration.
 * @param {string|React.ReactNode} [props.primaryButton.label='Confirm'] - Primary button text.
 * @param {Function} [props.primaryButton.onClick] - Primary button click handler.
 * @param {string} [props.primaryButton.variant='default'] - Button variant.
 * @param {string} [props.primaryButton.className] - Button className.
 * @param {boolean} [props.primaryButton.loading=false] - Loading state.
 * @param {boolean} [props.primaryButton.disabled=false] - Disabled state.
 * @param {React.ReactNode} [props.primaryButton.icon] - Button icon.
 * @param {Object} [props.secondaryButton] - Secondary button configuration.
 * @param {string|React.ReactNode} [props.secondaryButton.label='Cancel'] - Secondary button text.
 * @param {Function} [props.secondaryButton.onClick] - Secondary button click handler.
 * @param {string} [props.secondaryButton.variant='outline'] - Button variant.
 * @param {string} [props.secondaryButton.className] - Button className.
 * @param {boolean} [props.secondaryButton.disabled=false] - Disabled state.
 * @param {React.ReactNode} [props.secondaryButton.icon] - Button icon.
 * @param {boolean} [props.secondaryButton.hide=false] - Hide secondary button.
 * @param {boolean} [props.swapButtons=false] - In row layout, whether to swap primary & secondary order.
 * @param {React.ReactNode} [props.children] - Additional custom content.
 * @param {string} [props.className] - Custom container classes.
 * @param {boolean} [props.dragHandle=true] - Whether to show the top grabber/handle.
 * @param {boolean} [props.dismissible=true] - Whether drawer is dismissible by touch/drag.
 */
const MobileAlertDrawer = ({
  open,
  onOpenChange,
  onClose,
  title,
  icon: HeaderIcon,
  iconClassName,
  iconContainerClassName,
  titleClassName,
  showCloseButton = true,
  image,
  imageAlt = '',
  imagePosition = 'above-desc',
  imageClassName,
  description,
  descriptionClassName,
  description2,
  description2ClassName,
  buttonLayout = 'row',
  buttonGap = 'gap-3',
  primaryButton,
  secondaryButton,
  swapButtons = false,
  children,
  className,
  dragHandle = true,
  dismissible = true,
}) => {
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const isColumnLayout = buttonLayout === 'col' || buttonLayout === 'column';

  // Render Image Helper
  const renderImage = () => {
    if (!image) return null;
    if (typeof image === 'string') {
      return (
        <div className="flex justify-center my-2">
          <img
            src={image}
            alt={imageAlt}
            className={cn('max-h-36 max-w-full object-contain rounded-lg', imageClassName)}
          />
        </div>
      );
    }
    return <div className="flex justify-center my-2">{image}</div>;
  };

  // Render Icon Helper
  const renderHeaderIcon = () => {
    if (!HeaderIcon) return null;
    if (React.isValidElement(HeaderIcon)) {
      return HeaderIcon;
    }
    const IconComponent = HeaderIcon;
    return (
      <div
        className={cn(
          'flex items-center justify-center shrink-0 text-red-600',
          iconContainerClassName
        )}
      >
        <IconComponent className={cn('w-5 h-5 stroke-[2]', iconClassName)} />
      </div>
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
      <DrawerContent
        className={cn(
          'p-0 [&>div:first-child]:hidden top-auto !h-auto rounded-t-[22px] max-h-[90dvh] border-t border-zinc-100 z-[10002] bg-white shadow-2xl',
          className
        )}
      >
        <div className="flex flex-col bg-white rounded-t-[22px] select-none pb-6 px-5 pt-5">
          {/* Top Grabber Handle */}
          {/* {dragHandle && (
            <div className="w-10 h-1 rounded-full bg-zinc-300 mx-auto mb-3 shrink-0" />
          )} */}

          {/* Top image if position is 'above-header' */}
          {imagePosition === 'above-header' && renderImage()}

          {/* Header Row */}
          {(title || HeaderIcon || showCloseButton) && (
            <>
              <div className="flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  {renderHeaderIcon()}
                  {title && (
                    <h3
                      className={cn(
                        'text-lg font-semibold text-zinc-900 leading-tight truncate',
                        titleClassName
                      )}
                    >
                      {title}
                    </h3>
                  )}
                </div>

                {showCloseButton && (
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={handleClose}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200 transition-colors shrink-0 -mr-1 cursor-pointer"
                    aria-label="Close drawer"
                  >
                    <X className="w-5 h-5 stroke-[2]" />
                  </button>
                )}
              </div>

              {/* Header Divider Line */}
              <div className="h-[1px] bg-zinc-100 -mx-5 mt-3.5 mb-4 shrink-0" />
            </>
          )}

          {/* Body Content */}
          <div className="flex flex-col overflow-y-auto custom-scroll flex-1 py-1">
            {/* Image above description */}
            {imagePosition === 'above-desc' && renderImage()}

            {/* Major Description */}
            {description && (
              <div
                className={cn(
                  'text-[16px] font-semibold text-zinc-800 leading-relaxed',
                  descriptionClassName
                )}
              >
                {description}
              </div>
            )}

            {/* Optional Description 2 */}
            {description2 && (
              <div
                className={cn(
                  'text-sm text-zinc-500 mt-1.5 leading-normal',
                  description2ClassName
                )}
              >
                {description2}
              </div>
            )}

            {/* Image below description */}
            {imagePosition === 'below-desc' && renderImage()}

            {/* Custom Children Slot */}
            {children && <div className="mt-3">{children}</div>}
          </div>

          {/* Action Buttons */}
          {(primaryButton || secondaryButton) && (
            <div
              className={cn(
                'pt-5 mt-2 flex shrink-0 gap-3',
                isColumnLayout ? 'flex-col' : 'flex-row items-center',
                buttonGap
              )}
            >
              {/* Secondary Button */}
              {!secondaryButton?.hide && (
                <Button
                  type="button"
                  variant={secondaryButton?.variant || 'secondary'}
                  disabled={secondaryButton?.disabled}
                  onClick={(e) => {
                    if (secondaryButton?.onClick) {
                      secondaryButton.onClick(e);
                    } else {
                      handleClose();
                    }
                  }}
                  className={cn(
                    isColumnLayout
                      ? 'w-full order-2'
                      : swapButtons
                      ? 'flex-1 order-2'
                      : 'flex-1 order-1',
                    secondaryButton?.className
                  )}
                >
                  {secondaryButton?.icon && <span className="mr-1.5">{secondaryButton.icon}</span>}
                  {secondaryButton?.label || 'Cancel'}
                </Button>
              )}

              {/* Primary Button */}
              {primaryButton && (
                <Button
                  type="button"
                  variant={primaryButton?.variant || 'default'}
                  disabled={primaryButton?.disabled || primaryButton?.loading}
                  onClick={primaryButton?.onClick}
                  className={cn(
                    isColumnLayout
                      ? 'w-full order-1'
                      : swapButtons
                      ? 'flex-1 order-1'
                      : 'flex-1 order-2',
                    primaryButton?.className
                  )}
                >
                  {primaryButton?.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {primaryButton?.icon && <span className="mr-1.5">{primaryButton.icon}</span>}
                      {primaryButton?.label || 'Confirm'}
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileAlertDrawer;
